import React, { PropTypes, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useParams,
  useHistory,
  useRouteMatch,
  useLocation
} from "react-router-dom";

import _ from 'lodash';
import smartlookClient from 'smartlook-client';
import posthog from 'posthog-js';
import { fetchWorkspaces, fetchWorkspace } from 'actions/workspace';
import { fetchUser } from 'actions/users'

import { ToastContainer, toast } from "react-toastify";
import { IKContext } from 'imagekitio-react';
import { IntercomProvider } from 'react-use-intercom';
import { FontManager } from 'components/Font';
import WebSocket from 'components/WebSocket';
import ApplicationMenu from 'components/ApplicationMenu';

import SigninView from 'pages/anonymous/signin';
import StoryboardsView from 'pages/storyboards';
import IntegrationSettings from 'pages/settings/integration';
import PageView from 'pages/pages';
import GatingView from 'pages/pages/gating';
import { PreviewPage, EmbeddedPageView } from 'pages/pages/preview';
import { VersionPreview, EmbeddedVersionPreview } from 'pages/pages/versions';
import UserprofileView from 'pages/settings/userprofile';
import UserManagementView from 'pages/settings/usermanagement';
import { Helmet } from "react-helmet";

import AuthService from 'services/auth.service';
import AuthInterceptor from 'services/auth.interceptor';
import userRoleService from 'services/userRoles.service';
import 'react-toastify/dist/ReactToastify.css';
import ResetPassword from 'pages/anonymous/resetPassword';
import AcceptInvitation from 'pages/anonymous/acceptInvitation';
import { CreateTemplatePage } from 'pages/storyboards/templates/create';
import { CloneStoryboardPage } from 'pages/storyboards/clone';
import { Loader, ShortLoader } from 'components/ApplicationLoader';
import { Button } from "react-bootstrap";
import cleverStoryLogo from "../../assets/images/logo/logo-cs.svg";
import workspaceSpaceman from "../../assets/images/workspaceSpaceman.png"
import workspaceCloud from "../../assets/images/workspaceCloud.png"
import SignInAs from 'pages/anonymous/signInAs';
import SsoSuccess from 'pages/anonymous/ssoSuccess';
import SsoFailure from 'pages/anonymous/ssoFailure';
import LiveElementEditorPage from 'pages/liveElement';
import PaletteComponentEditorPage from 'pages/paletteComponent';
import { CloneLiveElementPage } from 'pages/liveElement/clone';
import { BroadcastChannelComponent } from 'components/Broadcaster';
import { FigmaAuthenticationCallback } from 'pages/settings/integration/figma';
import WorkspaceReports from 'pages/storyboards/storyboardSettings/workspaceReports/components';
import { createMixpanelEvent } from "actions/mixpanel";
import { getStoryboardMixpanelEventPayload, buildMixpanelPayload } from "services/mixpanelUtil";
import StoryboardCreationPage from 'pages/storyboards/storyboardCreation';
import StoryboardCreationPageView from 'pages/storyboards/storyboardCreation';
import styles from './application.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
const INTERCOM_ID = process.env.REACT_APP_CLEVERSTORY_INTERCOM_ID;
const SMARTLOOK_ID = process.env.REACT_APP_SMARTLOOK_ID;
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;

export default function Application(props) {

  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const dispatch = useDispatch();
  const workspaces = useSelector((state) => _.get(state, "workspaces.items", null));
  const profile = useSelector((state) => _.get(state, "users.me", null));

  var workspace = null;

  if (workspaces && workspaces.length > 0) {
    workspace = workspaces[0];
  }

  const activeWorkspace = useSelector(state => _.get(state, 'workspaces.workspace.item', null));

  useEffect(() => {
    if (SMARTLOOK_ID) {
      smartlookClient.init(SMARTLOOK_ID);
    } 
  }, [])

  useEffect(() => {
    const handleClick = (event) => {
      const { id: storyboardId, name: storyboardName, type: storyboardType } = storyboard || {};
      const target = event.target.closest("[data-track-props]");
      if (!target) return;
      const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardId, storyboardName, storyboardType);
      const fullPayload = buildMixpanelPayload(target, mixpanelPayload);

      dispatch(createMixpanelEvent("Button Clicked", fullPayload));
    };
    document.addEventListener("pointerdown", handleClick);
    return () => document.removeEventListener("pointerdown", handleClick);
  }, [storyboard]);

  useEffect(() => {
    if (SMARTLOOK_ID && profile && profile.email) {
      const name = `${_.get(profile, 'firstName', '')} ${_.get(profile, 'lastName', '')}`;
      smartlookClient.identify(profile.email, {
        name: name,
        email: profile.email,
        "Job Title": _.get(profile, 'title', '')
      })
    }
  }, [profile])

  useEffect(() => {
    if (!profile?.id || !profile?.email) return;

    if (!posthog.__loaded) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,

        autocapture: true,
        capture_pageview: true,

        persistence: "localStorage+cookie",
      });
    }

    if (posthog.get_distinct_id() !== String(profile.id)) {
      posthog.reset();
    }

    posthog.identify(String(profile.id), {
      email: profile.email,
      name: `${_.get(profile, "firstName", "")} ${_.get(profile, "lastName", "")}`,
      job_title: _.get(profile, "title", ""),

      workspace_id: workspace?.id,
      workspace_name: workspace?.name,
    });
  }, [profile, workspace]);

  const renderTitle = () => {
    if (workspaces && workspaces.length > 1 && activeWorkspace) {
      return `${activeWorkspace.name} - Cleverstory Application`;
    }

    return "Cleverstory Application";
  }

  return (
    <>
      <Helmet>
        <title>{renderTitle()}</title>
        <meta property="og:title" content={renderTitle()} />
        <meta property="og:type" content="website" />
        <script>
          {
            `(function(c,l,a,r,i,t,y){
              c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
              t=l.createElement(r);
              t.async=1;
              t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];
              y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "${CLARITY_ID}");`
          }
        </script>
      </Helmet>
      <Router>
        <Switch>
          <Route path="/login">
            <SigninView />
          </Route>
          <Route exact path="/__login__/:id">
            <SignInAs />
          </Route>
          <Route path="/sso/success">
            <SsoSuccess />
          </Route>
          <Route path="/sso/failure">
            <SsoFailure />
          </Route>
          <Route path="/logout">
            <SigninView />
          </Route>

          <Route path="/reset-password/:id">
            <ResetPassword />
          </Route>

          <Route path="/invitation/:id">
            <AcceptInvitation />
          </Route>

          <Route path="/integrations/figma/callback">
            <FigmaAuthenticationCallback />
          </Route>

          <ProtectedRoute>
            <ApplicationLoader>
              <IntercomProvider autoBootProps appId={INTERCOM_ID}>
                <Route path="/" exact>
                  {workspace &&
                    <Redirect to={`/app/${workspace.id}/storyboards`} />
                  }
                </Route>
                <Route path="/app/:id">
                  <Workspace />
                </Route>
              </IntercomProvider>
            </ApplicationLoader>
          </ProtectedRoute>
        </Switch>
      </Router>
    </>
  )
}

function ProtectedRoute({ children, ...rest }) {
  var authenticated = AuthService.isAuthenticated();

  return (
    <Route {...rest}
      render={() =>
        authenticated ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/login"
            }}
          />
        )
      }
    />
  );
}

function ApplicationLoader(props) {
  const dispatch = useDispatch();

  const workspaces = useSelector(state => _.get(state, 'workspaces.items', []));
  const loading = useSelector(state => _.get(state, 'workspaces.loading', false));
  const fetched = useSelector(state => _.get(state, 'workspaces.fetched', false))

  useEffect(() => {
    dispatch(fetchWorkspaces());
    dispatch(fetchUser());
  }, [])

  if (loading) {
    return (
      <ShortLoader title="Loading workspace..." />
    );
  }

  if (!loading && fetched && workspaces.length === 0) {
    return <AccessDenied />
  }

  return props.children;
}

function Workspace(props) {
  const dispatch = useDispatch();
  const location = useLocation();

  const [state, setState] = useState({
    userRoleInitialized: false,
  })

  let { path, url } = useRouteMatch();
  const { id } = useParams();
  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', {}));
  const loading = useSelector(state => _.get(state, 'workspaces.workspace.loading', false));

  var paths = location.pathname.split('/');
  paths.splice(0, 3);
  var currentPath = paths.join('/');
  var permission = userRoleService.requirePermission(currentPath, workspace);

  useEffect(() => {
    if (id) {
      dispatch(fetchWorkspace(id));
      AuthInterceptor.updateAppId(id);
    }
  }, [id])

  useEffect(() => {
    if (workspace) {
      userRoleService.initialise(workspace, () => {
        setState(state => {
          return {
            userRoleInitialized: true
          }
        })
      });
    }
  }, [workspace]);

  if (!state.userRoleInitialized || loading || !workspace) {
    return (
      <div className={styles.loaderContainer}>
        <Loader title="Loading workspace..." />
      </div>
    );
  }

  return (
    <IKContext urlEndpoint={IMAGE_CDN_ENDPOINT}>
      <WebSocket>
        <Switch>
          <Route path={`${path}/storyboards/templates/:template/create`} exact>
            <CreateTemplatePage />
          </Route>
          <Route path={`${path}/storyboards/creation`}>
            <StoryboardCreationPageView />
          </Route>
          <Route path={`${path}/storyboards/:storyboard/clone`} exact>
            <CloneStoryboardPage />
          </Route>
          <Route path={`${path}/pages/:id`} exact>
            <PageView />
          </Route>
          <Route path={`${path}/pages/:id/preview`} exact>
            <PreviewPage />
          </Route>
          <Route path={`${path}/pages/:id/preview/embedded`} exact>
            <EmbeddedPageView />
          </Route>
          <Route path={`${path}/gating/:id`} exact>
            <GatingView />
          </Route>
          <Route path={`${path}/liveElement/:id`} exact>
            <LiveElementEditorPage />
          </Route>
          <Route path={`${path}/paletteComponent/:id`} exact>
            <PaletteComponentEditorPage/>
          </Route>
          <Route path={`${path}/liveElement/:id/clone`} exact>
            <CloneLiveElementPage />
          </Route>
          <Route path={`${path}/pages/:id/versions/:versionId`} exact>
            <VersionPreview />
          </Route>
          <Route path={`${path}/pages/:id/versions/:versionId/embedded`} exact>
            <EmbeddedVersionPreview />
          </Route>
          <Route>
            <ApplicationMenu />
            {permission &&
              <div className={styles.content}>
                <Route path={`${path}/storyboards`}>
                  <StoryboardsView />
                </Route>
                <Route path={`${path}/integration-settings`}>
                  <IntegrationSettings />
                </Route>
                <Route path={`${path}/settings/user-profile`}>
                  <UserprofileView />
                </Route>
                <Route path={`${path}/settings/user-management`}>
                  <UserManagementView />
                </Route>
                <Route path={`${path}/reports`}>
                  <WorkspaceReports />
                </Route>
              </div>
            }
            {!permission &&
              <AccessDenied />
            }
          </Route>
        </Switch>
        <FontManager />
        <ToastContainer
          autoClose={false}
          closeButton={false}
          position={toast.POSITION.BOTTOM_RIGHT}
          hideProgressBar={true}
          toastClassName={styles.toast}
          bodyClassName={styles.toastBody}
          className={styles.toastContainer}
        />
        <BroadcastChannelComponent />
      </WebSocket>
    </IKContext>
  )
}

function AccessDenied() {

  return (
    <div className={styles.errorMessageContainer}>
      <div className={styles.errorMessageWrapper}>
        <img src={workspaceSpaceman} className={styles.workspaceSpaceman} />
        <img src={workspaceCloud} className={styles.workspaceCloud} />
        <div className={styles.errorMessageContent}>
          <img src={cleverStoryLogo} className={styles.errorMessageLogo} />
          <div className={styles.errorMessageTitle}>
            <span>Seat</span>
            <span>not found</span>
          </div>
          <div className={styles.errorMessageSubText}>
            <span>Looks like there’s no seat on this workspace with your name on it. Please contact your admin to get access.</span>
            <Button href="/login" className={styles.logoutBtn}>Logout</Button>
          </div>
        </div>
      </div>
    </div>
  )
}